import { request } from "@/utilities/libs/request.ts";
import {
  IRequestArgs,
  IResponseData,
  ListResponseData,
} from "@/utilities/types/requests.ts";
import { ERequestMethods } from "@/utilities/enums/RequestMethod.ts";
import { E_ENQUIRY } from "../enums/EnquiryEntries";
import {
  EActionTrackingType,
  ICeaDetails,
  IEnquiryEntry,
  IEnquiryEntryLogAction,
  IEnquiryEntryUpdateDTO,
  IEnquirySubmissionLog,
  LeadFeedbackStatus,
} from "../types/EnquiryEntry";
import { TMonthlyLeadSummaryResponse } from "../types/MonthlyLeadSummary";
import EXPORT_DATA from "../enums/ExportData";

function getList({ queryParams }: IRequestArgs, leadId?: string) {
  let query = {
    sort: queryParams?.query?.sort,
    ...queryParams?.query?.search,
    ...queryParams?.pagination,
    excludeSpamLeads: true,
    contactSaleSubmissionId: leadId,
  };

  queryParams?.query?.filter?.forEach((item) => {
    query = {
      ...query,
      ...item,
    };
  });

  return request<ListResponseData<IEnquiryEntry>>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.MAIN,
    params: query,
  });
}

function getListByGCLID({ queryParams }: IRequestArgs) {
  let query = {
    sort: queryParams?.query?.sort,
    ...queryParams?.query?.search,
    ...queryParams?.pagination,
    isGclid: true,
  };

  queryParams?.query?.filter?.forEach((item) => {
    query = {
      ...query,
      ...item,
    };
  });

  return request<ListResponseData<IEnquiryEntry>>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.MAIN,
    params: query,
  });
}

function getListByAgency({ queryParams }: IRequestArgs) {
  let query = {
    sort: queryParams?.query?.sort,
    ...queryParams?.query?.search,
    ...queryParams?.pagination,
    excludeSpamLeads: true,
  };

  queryParams?.query?.filter?.forEach((item) => {
    query = {
      ...query,
      ...item,
    };
  });

  return request<ListResponseData<IEnquiryEntry>>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.MAIN + "/by-agency",
    params: query,
  });
}

function getListFeedbackByAgency({ queryParams }: IRequestArgs) {
  let query = {
    sort: queryParams?.query?.sort,
    ...queryParams?.query?.search,
    ...queryParams?.pagination,
    isNotFeedback: true,
    excludeWhatsAppLeads: true,
    excludeAgentLeads: true,
    excludeSpamLeads: true,
  };

  queryParams?.query?.filter?.forEach((item) => {
    query = {
      ...query,
      ...item,
    };
  });

  return request<ListResponseData<IEnquiryEntry>>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.MAIN + "/by-agency",
    params: query,
  });
}

function getListFeedbackByUser({ queryParams }: IRequestArgs) {
  let query = {
    sort: queryParams?.query?.sort,
    ...queryParams?.query?.search,
    ...queryParams?.pagination,
    isNotFeedback: true,
    excludeWhatsAppLeads: true,
    excludeAgentLeads: true,
    excludeSpamLeads: true,
  };

  queryParams?.query?.filter?.forEach((item) => {
    query = {
      ...query,
      ...item,
    };
  });

  return request<ListResponseData<IEnquiryEntry>>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.MAIN,
    params: query,
  });
}

function getById(id: string) {
  return request<IResponseData<IEnquiryEntry>>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.DETAIL.replace(":id", id),
  });
}

function getByToken(token: string) {
  return request<ListResponseData<IEnquiryEntry>>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.PUBLIC,
    params: { token },
  });
}

function getMonthlyLeadSummary(params: {
  month: number;
  year: number;
  page?: number;
  limit?: number;
  userName?: string;
  sort?: Record<string, "ASC" | "DESC">;
}) {
  return request<TMonthlyLeadSummaryResponse>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.MONTHLY_SUMMARY,
    params,
  });
}

function getAllLeadsCSVfile(filters: any) {
  const query = {
    sort: {
      createdAt: "DESC",
    },
    ...filters,
  };
  return request<any>({
    method: ERequestMethods.GET,
    url: EXPORT_DATA.CONTACT_SALE,
    params: query,
  });
}

function update(id: string, data: IEnquiryEntryUpdateDTO) {
  return request<IResponseData<IEnquiryEntry>>({
    method: ERequestMethods.PATCH,
    url: E_ENQUIRY.UPDATE.replace(":id", id),
    data,
  });
}

function updateFeedback(id: string, feedbackStatus: LeadFeedbackStatus) {
  return request<IResponseData<IEnquiryEntry>>({
    method: ERequestMethods.PATCH,
    url: E_ENQUIRY.UPDATE_FEEDBACK(id),
    data: { feedbackStatus },
  });
}

function deleteItem(id: string) {
  return request<IResponseData<IEnquiryEntry>>({
    method: ERequestMethods.DELETE,
    url: E_ENQUIRY.DETAIL.replace(":id", id),
  });
}

function getLogsById(id: string) {
  return request<ListResponseData<IEnquirySubmissionLog>>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.LOGS.replace(":id", id),
  });
}

function getLogsByToken(token: string) {
  return request<ListResponseData<IEnquirySubmissionLog>>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.LOGS_BY_TOKEN,
    params: { token },
  });
}

async function getCeaDetails(getBy: {
  phoneNumber?: string;
  email?: string;
}): Promise<ICeaDetails | null> {
  const response = await request<IResponseData<ICeaDetails>>({
    method: ERequestMethods.POST,
    url: E_ENQUIRY.GET_CEA_DETAILS,
    data: getBy,
  });

  if (!response.data) {
    throw new Error("CEA Details not found");
  }

  return response.data;
}

async function getLogActions(requestArgs: IRequestArgs, submissionId?: string) {
  const { queryParams } = requestArgs;

  return request<ListResponseData<IEnquiryEntryLogAction>>({
    method: ERequestMethods.GET,
    url: E_ENQUIRY.ACTION_LOGS,
    params: {
      submissionId,
      ...queryParams?.pagination,
    },
  });
}

async function createLogAction({
  submissionId,
  trackingType,
}: {
  submissionId: string;
  trackingType: EActionTrackingType;
}) {
  return request<IResponseData<IEnquiryEntryLogAction>>({
    method: ERequestMethods.POST,
    url: E_ENQUIRY.CREATE_ACTION_LOGS.replace(":id", submissionId),
    data: {
      actionTrackingType: trackingType,
    },
  });
}

const EnquiryEntriesRepository = Object.freeze({
  getList,
  getListByGCLID,
  getById,
  getByToken,
  getMonthlyLeadSummary,
  deleteItem,
  getListByAgency,
  getListFeedbackByAgency,
  getListFeedbackByUser,
  getLogsById,
  getLogsByToken,
  getCeaDetails,
  update,
  updateFeedback,
  createLogAction,
  getLogActions,
  getAllLeadsCSVfile,
});

export default EnquiryEntriesRepository;
